// Pure subscription/paywall logic, kept out of components so the rules that
// decide whether someone sees a locked door are testable.
//
// The bias throughout is toward NOT locking: an unreadable status, a network
// failure, or a missing field all resolve to "let them in". A paying family
// wrongly shown a paywall is a support ticket and a refund request; a free
// user briefly seeing a Premium chart is a rounding error.

export const TRIAL_URGENT_DAYS = 5;

export function normaliseStatus(data) {
  if (!data || typeof data !== 'object') return null;
  const trial = (data.trial && typeof data.trial === 'object') ? data.trial : {};
  return {
    tier: data.tier || 'free',
    hasPremiumAccess: data.has_premium_access !== false,
    gatingEnabled: data.gating_enabled === true,
    isAdmin: data.is_admin === true,
    trialActive: trial.active === true,
    trialDaysLeft: Number.isFinite(Number(trial.days_left)) ? Number(trial.days_left) : 0,
    trialEndsAt: typeof trial.ends_at === 'string' ? trial.ends_at : null,
    premiumFeatures: Array.isArray(data.premium_features) ? data.premium_features : [],
  };
}

/** Should this feature be shown as locked? Defaults to unlocked. */
export function isLocked(status, feature) {
  if (!status) return false;              // unknown state never locks
  if (!status.gatingEnabled) return false;
  if (status.isAdmin) return false;
  if (status.hasPremiumAccess) return false;
  if (!feature) return true;
  return status.premiumFeatures.includes(feature);
}

/** A 402 from any endpoint means "this needs Premium" - identify it safely. */
export function premiumErrorFeature(err) {
  const res = err && err.response;
  if (!res || res.status !== 402) return null;
  const detail = res.data && res.data.detail;
  if (detail && typeof detail === 'object' && detail.error === 'premium_required') {
    return detail.feature || 'premium';
  }
  return 'premium';
}

/**
 * Trial countdown chip state. Shown only during an active trial, and only
 * emphasised near the end - a banner shouting "29 days left!" on day one
 * teaches people to ignore it by the time it matters.
 */
export function trialBadge(status) {
  if (!status || !status.trialActive) return { show: false, daysLeft: 0, urgent: false };
  const d = status.trialDaysLeft;
  return { show: true, daysLeft: d, urgent: d > 0 && d <= TRIAL_URGENT_DAYS };
}


// How long before the trial ends the one Lists banner appears (V19.03, Ivan).
export const TRIAL_BANNER_DAYS = 3;

/**
 * What the app says about the Premium trial (V19.03, Ivan's decision on the
 * full test, item 23). Only while Premium gating is switched on - with it off
 * nothing locks, so a countdown would be a false warning.
 *
 *   phase 'trial'  : during the trial  -> days left, end date
 *   phase 'ended'  : trial over, no Premium -> "Your free trial has ended"
 *   phase null     : nothing to say
 */
export function trialMessage(status) {
  if (!status || !status.gatingEnabled || status.isAdmin) return { phase: null };
  if (status.trialActive) {
    const d = status.trialDaysLeft;
    return {
      phase: 'trial', daysLeft: d, endsAt: status.trialEndsAt,
      banner: d > 0 && d <= TRIAL_BANNER_DAYS,
    };
  }
  if (status.tier === 'free' && !status.hasPremiumAccess) return { phase: 'ended' };
  return { phase: null };
}
